import { isErrorWithData } from '@/services/helpers';
import {
  useGetSystemLossThresholdsQuery,
  useUpdateSystemLossThresholdsMutation,
} from '@/services/systemLossThresholds';
import { PalmettoButton, PalmettoModal } from '@palmetto/dsm-components';
import { Alert, toast } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useBlocker } from 'react-router-dom';

import { ChangeHistory } from './ChangeHistory';
import {
  areAllStatesUsed,
  DEFAULT_FORM_VALUES,
  EMPTY_STATE,
  SystemLossThresholdsFormValues,
  toFormValues,
  toPayload,
} from './formModel';
import { StateRow } from './StateRow';

export const SystemLossThresholds = () => {
  const { data, isLoading, isError: isLoadFailure } = useGetSystemLossThresholdsQuery();
  const [updateThresholds, { isLoading: isSaving }] = useUpdateSystemLossThresholdsMutation();
  const [serverError, setServerError] = useState<string>();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<SystemLossThresholdsFormValues>({
    mode: 'onSubmit',
    defaultValues: DEFAULT_FORM_VALUES,
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'entries',
  });

  const watchedEntries = useWatch({ control, name: 'entries' });
  const noStatesLeft = areAllStatesUsed(watchedEntries);

  useEffect(() => {
    if (data) reset(toFormValues(data));
  }, [data, reset]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => isDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const handleCancel = useCallback(() => {
    setServerError(undefined);
    reset(toFormValues(data));
  }, [reset, data]);

  const handleAddState = useCallback(() => append({ ...EMPTY_STATE, counties: [] }), [append]);

  const onSubmit = useCallback(
    async (values: SystemLossThresholdsFormValues) => {
      setServerError(undefined);
      try {
        const saved = await updateThresholds(toPayload(values)).unwrap();
        reset(toFormValues(saved));
        toast.success('System loss thresholds saved');
      } catch (err) {
        setServerError(
          isErrorWithData(err) ? err.data.message : 'Failed to save system loss thresholds. Please try again.',
        );
      }
    },
    [updateThresholds, reset],
  );

  const onInvalid = useCallback(() => {
    toast.error('Unable to save thresholds. Please fix the highlighted fields and try again');
  }, []);

  const handleBlockerReset = useCallback(() => blocker.reset?.(), [blocker]);
  const handleBlockerProceed = useCallback(() => blocker.proceed?.(), [blocker]);

  const isDisabled = isLoading || isSaving;

  return (
    <div className="flex flex-col gap-16">
      <div className="flex flex-wrap items-center justify-between gap-8">
        <p className="text-sm text-text-secondary">
          Expected minimum system loss and snow loss by state, with optional county overrides. Percentages are 0 to
          100. Leave snow loss blank for locations with no snow component.
        </p>
        <div className="flex gap-4">
          <PalmettoButton
            type="button"
            emphasis="secondary"
            layout="hug"
            onClick={handleCancel}
            disabled={!isDirty || isSaving}
          >
            Cancel
          </PalmettoButton>
          <PalmettoButton
            type="submit"
            form="system-loss-thresholds-form"
            emphasis="primary"
            layout="hug"
            disabled={isDisabled || isLoadFailure || !isDirty}
          >
            {isSaving ? 'Saving...' : 'Save Thresholds'}
          </PalmettoButton>
        </div>
      </div>

      {serverError && <Alert variant="danger" title="Could not save thresholds" message={serverError} />}

      {isLoadFailure && <p className="text-text-error">Failed to load system loss thresholds. Please refresh.</p>}

      {!isLoadFailure && (
        <form id="system-loss-thresholds-form" noValidate onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <div className="flex flex-col gap-8">
            {isLoading && <p className="text-sm text-text-secondary">Loading...</p>}
            {fields.map((field, index) => (
              <StateRow
                key={field.id}
                index={index}
                control={control}
                onRemove={remove}
                hasCountyErrors={!!errors.entries?.[index]?.counties}
                disabled={isDisabled}
              />
            ))}
            <div>
              <PalmettoButton
                type="button"
                emphasis="primary"
                layout="hug"
                onClick={handleAddState}
                disabled={isDisabled || noStatesLeft}
              >
                Add state
              </PalmettoButton>
            </div>
          </div>
        </form>
      )}

      {!isLoadFailure && <ChangeHistory />}

      {blocker.state === 'blocked' && (
        <PalmettoModal open onClose={handleBlockerReset}>
          <p>You have unsaved changes. Are you sure you want to leave?</p>
          <div className="mt-16 flex justify-end gap-8">
            <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleBlockerReset}>
              Stay
            </PalmettoButton>
            <PalmettoButton type="button" emphasis="primary" layout="hug" onClick={handleBlockerProceed}>
              Leave anyway
            </PalmettoButton>
          </div>
        </PalmettoModal>
      )}
    </div>
  );
};
